import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Search, Eye } from "lucide-react";
import { toast } from "sonner";

const Input = (p) => <input {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 w-full" />;
const Label = ({ children }) => <label className="text-sm font-medium text-slate-700 block mb-1">{children}</label>;

export default function Consignors() {
  const { store } = useStore();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", default_payout_pct: "" });

  const load = () => {
    setRows(null);
    api.get("/consignors", { params: { search, status } }).then((r) => setRows(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, status, store?.id]);

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      await api.post("/consignors", { ...form, default_payout_pct: form.default_payout_pct ? Number(form.default_payout_pct) : null });
      toast.success("Consignor added");
      setOpen(false); setForm({ name: "", email: "", phone: "", default_payout_pct: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cur = store?.currency || "USD";

  return (
    <div>
      <PageHeader title="Consignors" subtitle="Everyone who consigns with your store">
        <button data-testid="add-consignor-btn" onClick={() => setOpen(true)}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg text-sm transition">
          <Plus className="w-4 h-4" /> Add Consignor
        </button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="consignor-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search consignors…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm w-full outline-none focus:border-emerald-500" />
        </div>
        <select data-testid="consignor-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white outline-none">
          <option value="all">All statuses</option><option value="active">Active</option><option value="archived">Archived</option>
        </select>
      </div>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <EmptyState icon={Users} title="No consignors yet" description="Add your first consignor to start tracking payouts." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3 text-center">Payout</th><th className="px-4 py-3 text-center">Active</th>
                <th className="px-4 py-3 text-center">Sold</th><th className="px-4 py-3 text-right">Pending Balance</th>
                <th className="px-4 py-3">Last Settlement</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c, i) => (
                <tr key={c.id} data-testid={`consignor-row-${i}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/consignors/${c.id}`)}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{c.name}</td>
                  <td className="px-4 py-3 text-slate-500"><div>{c.email || "—"}</div><div className="text-xs">{c.phone}</div></td>
                  <td className="px-4 py-3 text-center font-mono-fin">{c.default_payout_pct}%</td>
                  <td className="px-4 py-3 text-center">{c.active_items}</td>
                  <td className="px-4 py-3 text-center">{c.sold_items}</td>
                  <td className="px-4 py-3 text-right font-mono-fin font-semibold text-amber-700">{money(c.pending_balance, cur)}</td>
                  <td className="px-4 py-3 text-slate-500">{c.last_settlement || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-right"><Eye className="w-4 h-4 text-slate-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-consignor-dialog">
          <DialogHeader><DialogTitle>Add Consignor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="consignor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input data-testid="consignor-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input data-testid="consignor-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>Default payout % (blank = store default {store?.default_payout_pct}%)</Label>
              <Input data-testid="consignor-payout-input" type="number" value={form.default_payout_pct} onChange={(e) => setForm({ ...form, default_payout_pct: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm">Cancel</button>
            <button data-testid="save-consignor-btn" onClick={save} className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
